import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  Output,
  SimpleChanges,
  signal,
  ViewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableDataSource } from '@angular/material/table';
import { CdkVirtualScrollViewport, ScrollingModule } from '@angular/cdk/scrolling';
import { filter, take, Subscription } from 'rxjs';
import { SharedModule } from 'src/app/shared/shared.module';
import { MaterialModule } from 'src/app/shared/material.module';
import { ImportServiceV3 } from 'src/app/services/imports/import.service-v3';
import { CommonService } from 'src/app/services/common/common.service';
import { SnackbarComponent } from 'src/app/shared/components/snackbar/snackbar.component';
import { ImportacaoLinha, ImportacaoLinhaErro } from 'src/models/interfaces/importação/upload-importacao-interface';
import { StatusProcessamento } from 'src/models/interfaces/importação/status.enum';
import { IsEditedPipe } from './is-edited.pipe';
import { ImportServiceV3LoadingKeys } from 'src/models/keys/imports/imports.keys';

// --- Types ---

export interface EnrichedCell {
  campo: string;
  tipo: string;
  obrigatorio: boolean;
  valor: string;
  error: ImportacaoLinhaErro | null;
  errorId: number | null;
  errorIds: number[];
}

export type ImportacaoLinhaEnriched = ImportacaoLinha & {
  _errorMap: Map<string, ImportacaoLinhaErro[]>;
  _enrichedCells: EnrichedCell[];
};

export interface ColumnMapping {
  nomeCabecalhoOficial: string;
  nomeCabecalhoArquivo: string;
  _index?: number;
}

@Component({
  selector: 'spe-csv-step-processing',
  templateUrl: './step-processing.component.html',
  styleUrls: ['./step-processing.component.scss'],
  imports: [MaterialModule, SharedModule, ScrollingModule, IsEditedPipe],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class StepProcessingComponent {
  private readonly destroyRef = inject(DestroyRef);

  @Input() showStepError: boolean = false;
  @Input() showStepFail: boolean = false;
  @Input() alertTitle: string = '';
  @Input() alertMessage: string = '';
  @Input() alertError: string = '';
  @Input() alertStatus: string | null = null;
  @Input() showProcessing: boolean = false;
  @Input() isChecking?: boolean;
  @Input() checked?: boolean;
  @Input() showStepCounters: boolean = false;

  @Input() idImportacao?: number;
  @Input() statusImport: StatusProcessamento | null = null;
  @Input() columnsNotIdentified: string[] = [];
  @Input() columnsNotInFile: string[] = [];
  @Input() requiredColumns: string[] = [];

  @Input() showStepAlert: boolean = false;
  @Input() employees: number = 0;
  @Input() benefits: number = 0;
  @Input() errors: number = 0;
  @Input() set errorsToShow(value: ImportacaoLinha[]) {
    this.dataSource.data = this.enrichRows(value);
    this.snapshotFilteredRows();
    this.rebuildErrorList();
  }
  get errorsToShow(): ImportacaoLinhaEnriched[] {
    return this.dataSource.data as ImportacaoLinhaEnriched[];
  }

  @Output() eventNewImport = new EventEmitter<void>();
  @Output() eventShowStepFail = new EventEmitter<boolean>();
  @Output() eventShowStepError = new EventEmitter<boolean>();
  @Output() eventShowStepResult = new EventEmitter<void>();
  @Output() eventErrorsToShow = new EventEmitter<ImportacaoLinha[]>();
  @Output() eventQuantidadeLinhasComErro = new EventEmitter<number>();
  @Output() eventEditedLines = new EventEmitter<{ idLinha: number; mapLinha: Record<string, string> }[]>();

  @Input() columnMappings: ColumnMapping[] = [];
  @Output() resolvedCountChange = new EventEmitter<number>();
  @Output() requiredConfirmedCountChange = new EventEmitter<number>();

  dataSource = new MatTableDataSource<ImportacaoLinhaEnriched>([]);
  columns: string[] = [];

  // --- Signals ---
  readonly errorList = signal<number[]>([]);
  readonly errorIndex = signal<number>(-1);
  readonly editedIds = signal<ReadonlySet<number>>(new Set());
  readonly errorCounts = signal<Record<string, number>>({});
  readonly errorCountsTotal = signal<Record<string, number>>({});
  readonly linhasFaltantes = signal<number>(0);
  readonly filteredRows = signal<ImportacaoLinhaEnriched[]>([]);

  // --- Computed ---
  readonly actualErrorId = computed(() => this.errorList()[this.errorIndex()] ?? 0);
  readonly isFirstError = computed(() => this.errorIndex() <= 0);
  readonly isLastError = computed(() => {
    const list = this.errorList();
    return list.length === 0 || this.errorIndex() >= list.length - 1;
  });
  readonly errorColumnsComputed = computed(() => {
    const data = this.dataSource.data;
    if (!data.length) return [];
    const counts = this.errorCounts();
    const totals = this.errorCountsTotal();
    return data[0].listaDadosLinha
      .map((d) => d.campo)
      .filter((c) => (totals[c] ?? 0) > 0)
      .map((c) => ({
        campo: c,
        label: this.mapFilters['column'][c] || c,
        count: counts[c] ?? 0,
        countTotal: totals[c] ?? 0
      }));
  });

  // --- Staging buffer ---
  readonly errorListToEditMap = new Map<number, { idLinha: number; campo: string; valor: string }>();

  // --- Filters ---
  inputValue = '';
  showSearchBar = false;
  readonly activeFilters = signal<{ key: string; value: any }[]>([]);
  filtersForm = new FormGroup({
    column: new FormControl<string | null>(null)
  });
  mapFilters: Record<string, Record<string, string>> = {
    column: {}
  };

  // --- Column mapping ---
  columnState: Record<number, 'editing' | 'confirmed' | 'ignored'> = {};
  columnSelections: Record<number, string> = {};

  private isProgrammaticFocus = false;
  private hasChanged = false;
  private readonly durationInSeconds = 5;
  private loadSub?: Subscription;
  readonly ROW_HEIGHT = 35;
  private readonly HEADER_HEIGHT = 35;
  private readonly SCROLL_THICKNESS = 15;
  private readonly MAX_VIEWPORT_HEIGHT = 400;

  readonly viewportHeight = computed(() => {
    const rows = this.filteredRows().length;
    const contentHeight = rows * this.ROW_HEIGHT + this.HEADER_HEIGHT + this.SCROLL_THICKNESS;
    return Math.min(this.MAX_VIEWPORT_HEIGHT, contentHeight);
  });

  private _viewport?: CdkVirtualScrollViewport;
  private viewportSub?: Subscription;

  @ViewChild(CdkVirtualScrollViewport) set viewport(vp: CdkVirtualScrollViewport | undefined) {
    this.viewportSub?.unsubscribe();
    this._viewport = vp;
    if (vp) {
      this.viewportSub = vp.renderedRangeStream.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((range) => {
        const offset = `-${range.start * this.ROW_HEIGHT}px`;
        vp.elementRef.nativeElement.querySelectorAll('thead th').forEach((th: Element) => {
          (th as HTMLElement).style.top = offset;
        });
      });
    }
  }

  constructor(
    public readonly importServiceV3: ImportServiceV3,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private commonService: CommonService
  ) {
    this.setupFilterPredicate();

    this.filtersForm
      .get('column')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.updateCombinedFilter());

    this.filtersForm.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.activeFilters.set(this.listActiveFilters);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['showStepError']?.currentValue === true) {
      this.loadAllErrors();
    }
  }

  // --- Data loading ---

  loadAllErrors(): void {
    this.loadSub?.unsubscribe();
    this.errorListToEditMap.clear();
    this.editedIds.set(new Set());
    this.errorIndex.set(-1);
    this.showProcessing = true;
    this.checked = true;
    this.isChecking = false;
    this.loadSub = this.importServiceV3
      .getLinhasComErros(this.idImportacao!, 0, 99999, ImportServiceV3LoadingKeys.Default)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res) => {
        const enriched = this.enrichRows(res.retorno.content);
        this.dataSource.data = enriched;
        this.snapshotFilteredRows();
        this.rebuildErrorList(true);
        this.linhasFaltantes.set(this.errorList().length);
        this.eventQuantidadeLinhasComErro.emit(this.linhasFaltantes());

        const mapColumns = res.cabecalho.listaChavesCabecalho.reduce(
          (acc, item) => {
            acc[item.chave] = item.nomeOficial;
            return acc;
          },
          {} as Record<string, string>
        );
        this.columns = enriched[0]?.listaDadosLinha.map((d) => mapColumns[d.campo] || d.campo) ?? [];

        this.mapFilters['column'] = this.columns.reduce(
          (acc, col, i) => {
            acc[enriched[0].listaDadosLinha[i].campo] = col;
            return acc;
          },
          {} as Record<string, string>
        );

        this.eventErrorsToShow.emit(enriched);
        this.showProcessing = false;
        this.cdr.markForCheck();
      });
  }

  // --- Error navigation ---

  goToError(errorId: number): void {
    this.scrollToError(errorId);
  }

  setActualErrorId(value: number): void {
    const index = this.errorList().indexOf(value);
    if (index !== -1) {
      this.errorIndex.set(index);
    }
  }

  prevError(): void {
    const idx = this.errorIndex();
    if (this.hasChanged) {
      this.errorIndex.update((i) => i - 1);
      this.hasChanged = false;
    } else if (idx > 0) {
      this.errorIndex.update((i) => i - 1);
    }
    this.goToError(this.actualErrorId());
  }

  nextError(): void {
    if (this.isLastError()) return;
    const idx = this.errorIndex();
    const list = this.errorList();
    if (this.hasChanged) {
      if (idx === -2) {
        this.errorIndex.set(list.length - 1);
      }
      this.errorIndex.update((i) => i - 1);
      this.hasChanged = false;
    }

    if (idx === -1) {
      this.errorIndex.set(0);
      this.goToError(this.actualErrorId());
      return;
    }
    if (idx >= list.length) {
      this.errorIndex.set(list.length - 1);
    } else {
      this.errorIndex.update((i) => i + 1);
    }
    this.goToError(this.actualErrorId());
  }

  // --- Scroll ---

  scrollToError(errorId: number): void {
    const rows = this.filteredRows();
    const rowIndex = rows.findIndex((row) => row.errosLinha.some((e) => e.id === errorId));
    if (rowIndex === -1 || !this._viewport) return;

    const focusCell = () => {
      requestAnimationFrame(() => {
        const el = document.getElementById(String(errorId));
        if (!el) return;

        const textarea = el.querySelector('textarea') as HTMLElement | null;
        if (textarea) {
          this.isProgrammaticFocus = true;
          const viewportEl = this._viewport?.elementRef.nativeElement;
          const viewportRect = viewportEl?.getBoundingClientRect();
          const elRect = el.getBoundingClientRect();

          if (viewportRect && viewportEl) {
            const elCenterY = elRect.top + elRect.height / 2;
            const elCenterX = elRect.left + elRect.width / 2;
            const viewportCenterY = viewportRect.top + viewportRect.height / 2;
            const viewportCenterX = viewportRect.left + viewportRect.width / 2;

            const scrollAdjustY = elCenterY - viewportCenterY;
            const scrollAdjustX = elCenterX - viewportCenterX;

            this._viewport?.scrollTo({
              top: viewportEl.scrollTop + scrollAdjustY,
              left: viewportEl.scrollLeft + scrollAdjustX,
              behavior: 'smooth'
            });
          }
          textarea.focus({ preventScroll: true });
        }
      });
    };

    const range = this._viewport.getRenderedRange();
    const alreadyRendered = rowIndex >= range.start && rowIndex < range.end;

    this._viewport.scrollToIndex(rowIndex, 'smooth');

    if (alreadyRendered) {
      focusCell();
    } else {
      this._viewport.renderedRangeStream
        .pipe(
          filter((r) => rowIndex >= r.start && rowIndex < r.end),
          take(1),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe(() => focusCell());
    }
  }

  // --- Cell editing ---

  onCellBlur(cell: EnrichedCell, row: ImportacaoLinhaEnriched): void {
    this.hasChanged = true;
    if (!cell.errorId) return;

    for (const id of cell.errorIds) {
      const existing = this.errorListToEditMap.get(id);
      if (existing) {
        existing.valor = cell.valor;
      } else {
        this.errorListToEditMap.set(id, {
          idLinha: row.id,
          campo: cell.campo,
          valor: cell.valor
        });
      }
    }

    this.syncEditedIds();
    this.rebuildErrorList();
    this.eventQuantidadeLinhasComErro.emit(this.linhasFaltantes() - this.editedCellCount());
    this.eventEditedLines.emit(this.buildEditedLinesPayload());
    this.cdr.markForCheck();
  }

  onErrorFocus(event: FocusEvent): void {
    if (this.isProgrammaticFocus) {
      this.isProgrammaticFocus = false;
      return;
    }
    const td = event.currentTarget as HTMLElement;
    const errorId = td.getAttribute('id');
    if (errorId) {
      this.setActualErrorId(Number(errorId));
    }
  }

  onCellKeydown(event: KeyboardEvent, cell: EnrichedCell, row: ImportacaoLinhaEnriched): void {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    this.onCellBlur(cell, row);
    if (this.isLastError()) {
      (event.target as HTMLElement).blur();
    } else {
      this.nextError();
    }
  }

  // --- Column mapping ---

  irParaCorrecao(): void {
    if (this.statusImport === StatusProcessamento.CORRIGIR_CABECALHO) {
      this.showStepFail = true;
      this.eventShowStepFail.emit(true);
    } else if (this.statusImport === StatusProcessamento.CORRIGIR_DADOS) {
      this.showStepError = true;
      this.eventShowStepError.emit(true);
    }
  }

  confirmarTroca(index: number): void {
    const column = this.columnsNotIdentified[index];
    const selected = this.columnSelections[index];
    if (!selected) return;

    const existing = this.columnMappings.find((item) => item.nomeCabecalhoArquivo === column && item._index === index);
    if (existing) {
      existing.nomeCabecalhoOficial = selected;
    } else {
      this.columnMappings.push({
        nomeCabecalhoOficial: selected,
        nomeCabecalhoArquivo: column,
        _index: index
      });
    }

    this.columnState[index] = 'confirmed';
    this.emitResolvedCount();
  }

  ignorar(index: number): void {
    this.columnState[index] = 'ignored';
    this.emitResolvedCount();
  }

  editar(index: number): void {
    this.columnState[index] = 'editing';
    this.emitResolvedCount();
  }

  getState(index: number): string {
    return this.columnState[index] || 'editing';
  }

  getAvailableOptions(index: number): string[] {
    const selectedValues = new Set(
      Object.entries(this.columnSelections)
        .filter(([i, val]) => +i !== index && val)
        .map(([, val]) => val)
    );
    return this.columnsNotInFile.filter((opt) => !selectedValues.has(opt));
  }

  get resolvedCount(): number {
    return Object.values(this.columnState).filter((s) => s === 'confirmed' || s === 'ignored').length;
  }

  private emitResolvedCount(): void {
    this.resolvedCountChange.emit(this.resolvedCount);
    const confirmedRequired = this.columnMappings
      .filter((m) => this.columnState[m._index!] === 'confirmed')
      .filter((m) => this.requiredColumns.includes(m.nomeCabecalhoOficial)).length;
    this.requiredConfirmedCountChange.emit(confirmedRequired);
  }

  isRequiredSelected(index: number): boolean {
    return this.requiredColumns.includes(this.columnSelections[index]);
  }

  isRequired(option: string): boolean {
    return this.requiredColumns.includes(option);
  }

  // --- TrackBy ---

  trackByLinha(_index: number, row: ImportacaoLinhaEnriched): number {
    return row.numeroLinha;
  }

  trackByCell =
    (numeroLinha: number) =>
    (_index: number, cell: EnrichedCell): string =>
      `${numeroLinha}-${cell.campo}`;

  // --- Filter logic ---

  applySearchFilter = (): void => {
    this.updateCombinedFilter();
  };

  getFilterLabel(key: string, value: any): string {
    const map = this.mapFilters[key];
    return map?.[value] ?? `${key}: ${value}`;
  }

  clearFilter(key: string): void {
    this.errorIndex.set(-1);
    this.filtersForm.get(key)?.setValue(null);
  }

  clearAllFilters(): void {
    this.filtersForm.reset();
  }

  get listActiveFilters(): { key: string; value: any }[] {
    const formValues = this.filtersForm.value as Record<string, any>;
    return Object.entries(formValues)
      .filter(([_, value]) => value !== null && value !== undefined && value !== '')
      .map(([key, value]) => ({ key, value }));
  }

  // --- Private helpers ---

  private rebuildErrorList(isInitialLoad = false): void {
    const filtered = this.dataSource.filteredData as ImportacaoLinhaEnriched[];
    const allData = this.dataSource.data as ImportacaoLinhaEnriched[];
    const columnFilter = this.filtersForm.get('column')?.value;

    // Counts from ALL rows (ignoring column filter) — count by cell, not individual errors
    const counts: Record<string, number> = {};
    for (const row of allData) {
      for (const cell of row._enrichedCells) {
        if (cell.errorIds.length === 0) continue;
        if (cell.errorIds.every((id) => this.errorListToEditMap.has(id))) continue;
        counts[cell.campo] = (counts[cell.campo] ?? 0) + 1;
      }
    }

    // Navigation list from filtered rows, respecting column filter — one entry per cell with errors
    const ids: number[] = [];
    for (const row of filtered) {
      for (const cell of row._enrichedCells) {
        if (cell.errorIds.length === 0) continue;
        if (cell.errorIds.every((id) => this.errorListToEditMap.has(id))) continue;
        if (columnFilter && cell.campo !== columnFilter) continue;
        ids.push(cell.errorId!);
      }
    }

    this.errorList.set(ids);
    this.errorCounts.set(counts);
    if (isInitialLoad) {
      this.errorCountsTotal.set({ ...counts });
    }

    const idx = this.errorIndex();
    if (ids.length === 0) {
      this.errorIndex.set(-1);
    } else if (idx === ids.length - 1) {
      this.errorIndex.set(-2);
    }
  }

  private syncEditedIds(): void {
    this.editedIds.set(new Set(this.errorListToEditMap.keys()));
  }

  private editedCellCount(): number {
    const allData = this.dataSource.data as ImportacaoLinhaEnriched[];
    let count = 0;
    for (const row of allData) {
      for (const cell of row._enrichedCells) {
        if (cell.errorIds.length === 0) continue;
        if (cell.errorIds.every((id) => this.errorListToEditMap.has(id))) count++;
      }
    }
    return count;
  }

  private buildEditedLinesPayload(): { idLinha: number; mapLinha: Record<string, string> }[] {
    const grouped = new Map<number, Record<string, string>>();
    for (const { idLinha, campo, valor } of this.errorListToEditMap.values()) {
      let mapLinha = grouped.get(idLinha);
      if (!mapLinha) {
        mapLinha = {};
        grouped.set(idLinha, mapLinha);
      }
      mapLinha[campo] = valor;
    }
    return Array.from(grouped, ([idLinha, mapLinha]) => ({ idLinha, mapLinha }));
  }

  private snapshotFilteredRows(): void {
    this.filteredRows.set(this.dataSource.filteredData as ImportacaoLinhaEnriched[]);
  }

  private enrichRows(rows: ImportacaoLinha[]): ImportacaoLinhaEnriched[] {
    return rows.map((row) => {
      const errorMap = new Map<string, ImportacaoLinhaErro[]>();
      for (const err of row.errosLinha) {
        const list = errorMap.get(err.campo);
        if (list) {
          list.push(err);
        } else {
          errorMap.set(err.campo, [err]);
        }
      }
      return {
        ...row,
        _errorMap: errorMap,
        _enrichedCells: row.listaDadosLinha.map((d) => {
          const errors = errorMap.get(d.campo) ?? [];
          if (errors.length === 0) {
            return {
              campo: d.campo,
              tipo: d.tipo,
              obrigatorio: d.obrigatorio,
              valor: d.valor,
              error: null,
              errorId: null,
              errorIds: []
            };
          }
          const mergedError: ImportacaoLinhaErro = {
            ...errors[0],
            mensagemErro: this.joinErrorMessages(errors.map((e) => e.mensagemErro))
          };
          return {
            campo: d.campo,
            tipo: d.tipo,
            obrigatorio: d.obrigatorio,
            valor: d.valor,
            error: mergedError,
            errorId: errors[0].id,
            errorIds: errors.map((e) => e.id)
          };
        })
      };
    });
  }

  private joinErrorMessages(messages: string[]): string {
    const normalized = messages.map((m) => m.charAt(0).toLowerCase() + m.slice(1).replace(/\.$/, ''));
    const result =
      normalized.length <= 1
        ? normalized[0] || ''
        : normalized.slice(0, -1).join(', ') + ' e ' + normalized[normalized.length - 1];
    return result.charAt(0).toUpperCase() + result.slice(1) + '.';
  }

  private setupFilterPredicate(): void {
    this.dataSource.filterPredicate = (row: ImportacaoLinhaEnriched, filterJson: string) => {
      const { text, column } = JSON.parse(filterJson) as { text: string; column: string | null };
      if (column && !row._errorMap.has(column)) return false;
      if (text) {
        const haystack = this.commonService.removeDiacritics(row.listaDadosLinha.map((d) => d.valor).join(' '));
        return haystack.includes(text.toLowerCase());
      }
      return true;
    };
  }

  private updateCombinedFilter(): void {
    const filterObject = {
      text: this.commonService.removeDiacritics(this.inputValue),
      column: this.filtersForm.get('column')?.value ?? null
    };
    this.dataSource.filter = JSON.stringify(filterObject);
    this.snapshotFilteredRows();
    this.rebuildErrorList();
    this.cdr.markForCheck();
  }

  private showSnackMessage(message: string | null, isError = true): void {
    this.snackBar.openFromComponent(SnackbarComponent, {
      duration: this.durationInSeconds * 1000,
      data: {
        message: message ?? 'Valor inválido',
        errorMessage: isError
      }
    });
  }
}
